import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timer as TimerIcon,
  FitnessCenter as FitnessCenterIcon,
} from '@mui/icons-material';
import { sessionPlanAPI, SessionPlan } from '../api/session-plan.api';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const SessionPlansPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [sessionPlans, setSessionPlans] = useState<SessionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState<string>('all');
  const [skillLevel, setSkillLevel] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const isCoach = ['ADMIN', 'HEAD_COACH', 'COACH'].includes(user?.role || '');

  useEffect(() => {
    fetchSessionPlans();
  }, [page, category, skillLevel]);

  const fetchSessionPlans = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await sessionPlanAPI.getSessionPlans({
        page,
        limit: 9,
        category: category !== 'all' ? category : undefined,
        skillLevel: skillLevel !== 'all' ? skillLevel : undefined,
        search: searchQuery || undefined,
      });
      setSessionPlans(response.sessionPlans || []);
      setTotalPages(response.pagination?.pages || 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load session plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchSessionPlans();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this session plan?')) {
      return;
    }

    try {
      await sessionPlanAPI.deleteSessionPlan(id);
      fetchSessionPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete session plan');
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'warm-up':
        return 'warning';
      case 'technical':
        return 'primary';
      case 'tactical':
        return 'secondary';
      case 'physical':
        return 'success';
      case 'cool-down':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSkillLevelColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'primary';
      case 'advanced':
        return 'warning';
      case 'elite':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Session Plans Library
        </Typography>
        {isCoach && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/session-plans/create')}
          >
            Create Plan
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ flexGrow: 1, maxWidth: 300 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="warm-up">Warm-up</MenuItem>
            <MenuItem value="technical">Technical</MenuItem>
            <MenuItem value="tactical">Tactical</MenuItem>
            <MenuItem value="physical">Physical</MenuItem>
            <MenuItem value="cool-down">Cool-down</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Skill Level</InputLabel>
          <Select
            value={skillLevel}
            label="Skill Level"
            onChange={(e) => {
              setSkillLevel(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="elite">Elite</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : sessionPlans.length === 0 ? (
        <Alert severity="info">No session plans found</Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {sessionPlans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': { boxShadow: 6 },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {plan.title}
                      </Typography>
                      {isCoach && (
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/session-plans/${plan.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(plan.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {plan.category && (
                        <Chip
                          label={plan.category}
                          size="small"
                          color={getCategoryColor(plan.category)}
                        />
                      )}
                      {plan.skillLevel && (
                        <Chip
                          label={plan.skillLevel}
                          size="small"
                          color={getSkillLevelColor(plan.skillLevel)}
                        />
                      )}
                      {plan.isTemplate && (
                        <Chip label="Template" size="small" variant="outlined" />
                      )}
                    </Box>

                    {plan.description && (
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {plan.description.length > 120
                          ? `${plan.description.substring(0, 120)}...`
                          : plan.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      {plan.duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimerIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {plan.duration} min
                          </Typography>
                        </Box>
                      )}
                      {plan.drills && plan.drills.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FitnessCenterIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {plan.drills.length} drills
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/session-plans/${plan.id}`)}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SessionPlansPage;
